import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { PlayCircleFilled } from '@ant-design/icons';
import './must-watch-video.less';
import useIsPhoneWidth from '../../hooks/useIsPhoneWidth';

const HOLE_PADDING = 10;
/** layout can change for reasons we cannot observe (fonts, async header data) */
const REMEASURE_INTERVAL = 300;
/** grace period before we decide the target will never appear */
const TARGET_MISSING_TIMEOUT = 1500;
/** must match `.mwv-shield-hint`'s max-width in the .less file */
const HINT_MAX_WIDTH = 260;
/** minimum gap kept between the hint box and either viewport edge */
const HINT_VIEWPORT_MARGIN = 8;

interface Hole {
  top: number;
  left: number;
  width: number;
  height: number;
}

/**
 * The hint box is centred on the hole via a `left` value paired with
 * `transform: translateX(-50%)` in the .less file, so `left` here means the
 * box's horizontal *centre*, not its edge. Uncentred and unclamped, that
 * center-on-the-hole math runs the box off-screen whenever the target sits
 * near either edge (e.g. the bell in a narrow mobile header) — clamp it so
 * both edges of the (worst-case max-width) box stay within the viewport.
 */
function clampHintLeft(hole: Hole): number {
  const halfWidth = Math.min(HINT_MAX_WIDTH, window.innerWidth - HINT_VIEWPORT_MARGIN * 2) / 2;
  const ideal = hole.left + hole.width / 2;
  const min = HINT_VIEWPORT_MARGIN + halfWidth;
  const max = window.innerWidth - HINT_VIEWPORT_MARGIN - halfWidth;
  return Math.min(Math.max(ideal, min), max);
}

interface Props {
  /** selector of the element the spotlight hole is cut around */
  targetSelector: string;
  /**
   * Selectors that stay keyboard/focus reachable. Defaults to `targetSelector`.
   * Portalled overlays (the notification panel) are listed here so they remain
   * usable even though they render outside the hole.
   */
  allowedSelectors?: string[];
  hint?: string;
  /**
   * Called when the target element cannot be found. The host uses this to
   * fail open (opening the modal directly) instead of leaving the user with a
   * fully blocked page and nothing to click.
   */
  onTargetMissing?: () => void;
  /**
   * Phones: a bottom card over the overlay (title, hint, primary button)
   * so the way forward is a thumb-sized button, not just a pulsing icon in a
   * header corner. Its root carries `.mwv-shield-cta`, which the host lists
   * in `allowedSelectors`.
   */
  phoneAction?: {
    title?: string;
    label: string;
    count?: number;
    onClick: () => void;
  };
}

/**
 * Blocks every interaction with the page except the one element matched by
 * `targetSelector`.
 *
 * Rather than a single full-screen overlay (which would be fighting the
 * z-index of whatever stacking context the target lives in), the shield is
 * drawn as four rectangles around the target — a spotlight with a real hole.
 * The target keeps its natural stacking position and stays clickable no matter
 * how deeply nested it is.
 *
 * Pointer events are swallowed by the four panels; keyboard interaction and
 * focus escapes are blocked with capture-phase listeners.
 */
const InteractionShield: React.FC<Props> = ({
  targetSelector,
  allowedSelectors,
  hint,
  onTargetMissing,
  phoneAction,
}) => {
  const isPhone = useIsPhoneWidth();
  const showPhoneCard = isPhone && !!phoneAction?.label;
  const [hole, setHole] = useState<Hole | null>(null);
  const missingSince = useRef<number | null>(null);
  const reportedMissing = useRef(false);

  const measure = useCallback(() => {
    const el = document.querySelector(targetSelector);
    if (!el) {
      setHole(null);
      // Give the host layout a moment to mount the indicator before we
      // declare it missing.
      if (missingSince.current === null) {
        missingSince.current = Date.now();
      } else if (
        !reportedMissing.current &&
        Date.now() - missingSince.current > TARGET_MISSING_TIMEOUT
      ) {
        reportedMissing.current = true;
        onTargetMissing?.();
      }
      return;
    }
    missingSince.current = null;
    const rect = el.getBoundingClientRect();
    const next: Hole = {
      top: rect.top - HOLE_PADDING,
      left: rect.left - HOLE_PADDING,
      width: rect.width + HOLE_PADDING * 2,
      height: rect.height + HOLE_PADDING * 2,
    };
    // The poll below runs several times a second — only re-render on a real move.
    setHole((prev) =>
      prev &&
      prev.top === next.top &&
      prev.left === next.left &&
      prev.width === next.width &&
      prev.height === next.height
        ? prev
        : next
    );
  }, [targetSelector, onTargetMissing]);

  useEffect(() => {
    measure();
    const intervalId = window.setInterval(measure, REMEASURE_INTERVAL);
    window.addEventListener('resize', measure);
    window.addEventListener('scroll', measure, true);
    return () => {
      window.clearInterval(intervalId);
      window.removeEventListener('resize', measure);
      window.removeEventListener('scroll', measure, true);
    };
  }, [measure]);

  // Keyboard + focus lock: nothing outside the allowed elements may be typed
  // into, tabbed to or activated.
  const allowed = (allowedSelectors?.length
    ? allowedSelectors
    : [targetSelector]
  ).join(',');

  useEffect(() => {
    const isAllowed = (node: EventTarget | null) => {
      const el = node as HTMLElement | null;
      return !!el?.closest?.(allowed);
    };

    const blockKey = (event: KeyboardEvent) => {
      if (isAllowed(event.target)) {
        return;
      }
      event.preventDefault();
      event.stopPropagation();
    };

    const trapFocus = (event: FocusEvent) => {
      if (isAllowed(event.target)) {
        return;
      }
      const target = document.querySelector(targetSelector) as HTMLElement | null;
      (event.target as HTMLElement)?.blur?.();
      target?.focus?.();
    };

    document.addEventListener('keydown', blockKey, true);
    document.addEventListener('focusin', trapFocus, true);
    (document.activeElement as HTMLElement)?.blur?.();

    return () => {
      document.removeEventListener('keydown', blockKey, true);
      document.removeEventListener('focusin', trapFocus, true);
    };
  }, [targetSelector, allowed]);

  // Geometry has to be inline — it is measured at runtime. Every visual
  // property (colour, z-index, cursor, animation) lives in the .less file.
  const panels: Array<{ key: string; style: React.CSSProperties }> = hole
    ? [
        { key: 'top', style: { top: 0, left: 0, right: 0, height: Math.max(hole.top, 0) } },
        {
          key: 'bottom',
          style: { top: hole.top + hole.height, left: 0, right: 0, bottom: 0 },
        },
        {
          key: 'left',
          style: {
            top: hole.top,
            left: 0,
            width: Math.max(hole.left, 0),
            height: hole.height,
          },
        },
        {
          key: 'right',
          style: {
            top: hole.top,
            left: hole.left + hole.width,
            right: 0,
            height: hole.height,
          },
        },
      ]
    : [{ key: 'full', style: { inset: 0 } }];

  const swallow = (event: React.SyntheticEvent) => {
    event.preventDefault();
    event.stopPropagation();
  };

  return createPortal(
    <div className="must-watch-interaction-shield">
      {panels.map((panel) => (
        <div
          key={panel.key}
          className="mwv-shield-panel"
          style={panel.style}
          onClickCapture={swallow}
          onMouseDownCapture={swallow}
          onTouchStartCapture={swallow}
          onContextMenu={swallow}
        />
      ))}
      {hole && (
        <div
          className="mwv-shield-ring"
          style={{
            top: hole.top,
            left: hole.left,
            width: hole.width,
            height: hole.height,
          }}
        />
      )}
      {hint && hole && !showPhoneCard && (
        <div
          className="mwv-shield-hint"
          style={{
            top: hole.top + hole.height + 12,
            left: clampHintLeft(hole),
          }}
        >
          {hint}
        </div>
      )}
      {showPhoneCard && phoneAction && (
        <div className="mwv-shield-cta" role="dialog" aria-live="polite">
          <span className="mwv-shield-cta__icon">
            <PlayCircleFilled />
            {!!phoneAction.count && phoneAction.count > 1 && (
              <span className="mwv-shield-cta__count">{phoneAction.count}</span>
            )}
          </span>
          {!!phoneAction.title && (
            <div className="mwv-shield-cta__title">{phoneAction.title}</div>
          )}
          {!!hint && <div className="mwv-shield-cta__hint">{hint}</div>}
          <button
            type="button"
            className="mwv-shield-cta__btn"
            onClick={phoneAction.onClick}
          >
            <PlayCircleFilled />
            {phoneAction.label}
          </button>
        </div>
      )}
    </div>,
    document.body
  );
};

export default InteractionShield;
